import React, { Children, isValidElement } from 'react';
import { makeStyles } from 'tss-react/mui';

import ToggleButton from '@mui/material/ToggleButton';
import type { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';

import { buttonFocusVisibleStyles } from 'themes/muiComponents/buttonFocusTokens';
import { TOGGLE_BUTTON_GROUP_BUTTON_MARGIN_PX } from 'themes/muiComponents/muiToggle';

export type QSegmentedTabsSize = 'small' | 'medium' | 'large' | 'huge';

interface QSegmentedTabsProps extends Omit<ToggleButtonGroupProps, 'size'> {
  elevated?: boolean;
  size?: QSegmentedTabsSize;
  /** Direct `ToggleButton` children only. Do not wrap them in a Fragment. */
  children?: ToggleButtonGroupProps['children'];
}

const GROUP_RADIUS_TOKEN: Record<QSegmentedTabsSize, 'md' | 'lg' | 'xl'> = {
  small: 'md',
  medium: 'lg',
  large: 'xl',
  huge: 'xl',
};

const groupedButtonRadiusSelector = [
  `&.${toggleButtonGroupClasses.firstButton}`,
  `&.${toggleButtonGroupClasses.middleButton}`,
  `&.${toggleButtonGroupClasses.lastButton}`,
].join(', ');

const useStyles = makeStyles<{ elevated: boolean; size: QSegmentedTabsSize }>()((theme, { elevated, size }) => {
  const groupBorderRadius = theme.shape.radius[GROUP_RADIUS_TOKEN[size]];
  const tabBorderRadius = Math.max(0, groupBorderRadius - TOGGLE_BUTTON_GROUP_BUTTON_MARGIN_PX);

  return {
    qSegmentedTabs: {
      borderRadius: groupBorderRadius,
      backgroundColor: elevated ? theme.palette.background.layer1 : theme.palette.background.default,
      boxShadow: elevated ? theme.shadows[2] : theme.shadows[0],
      maxWidth: '100%',
      minWidth: 0,
      flexWrap: 'nowrap',
    },
    toggleButton: {
      minWidth: 0,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      backgroundColor: 'transparent',
      borderRadius: tabBorderRadius,
      [groupedButtonRadiusSelector]: {
        borderRadius: tabBorderRadius,
      },
      transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeInOut,
      }),
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&.Mui-focusVisible': {
        ...buttonFocusVisibleStyles(theme),
      },
      ...(size === 'small' && {
        padding: theme.spacing(0.5),
        minHeight: 26,
        ...theme.typography.captionMedium,
      }),
      ...(size === 'medium' && {
        padding: theme.spacing(0.75),
        minHeight: 32,
        ...theme.typography.paragraphMedium,
      }),
      ...(size === 'large' && {
        padding: theme.spacing(1),
        minHeight: 36,
        ...theme.typography.bodyMedium,
      }),
      ...(size === 'huge' && {
        padding: theme.spacing(1.5),
        minHeight: 40,
        ...theme.typography.bodyMedium,
      }),
      '& svg, & .MuiSvgIcon-root': {
        flexShrink: 0,
        width: size === 'small' ? 16 : size === 'medium' ? 18 : size === 'large' ? 20 : 24,
        height: size === 'small' ? 16 : size === 'medium' ? 18 : size === 'large' ? 20 : 24,
        fontSize: size === 'small' ? 16 : size === 'medium' ? 18 : size === 'large' ? 20 : 24,
      },
    },
    toggleButtonLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'nowrap',
      gap: theme.spacing(0.75),
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      '& .MuiTypography-root': {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    toggleButtonLabelText: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    selected: {
      backgroundColor: `${theme.palette.background.paper} !important`,
      boxShadow: theme.shadows[2],
      '&:hover': {
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '&.Mui-focusVisible': {
        ...buttonFocusVisibleStyles(theme),
        boxShadow: `${theme.palette.focusRings.button}, ${theme.shadows[2]}`,
      },
    },
  };
});

const QSegmentedTabs = ({
  className,
  children,
  onChange,
  elevated = false,
  size = 'medium',
  ...otherProps
}: QSegmentedTabsProps) => {
  const { classes, cx } = useStyles({ elevated, size });
  const muiSize = size === 'huge' ? undefined : size;
  const tabChildren = Children.toArray(children).filter(isValidElement);

  return (
    <ToggleButtonGroup
      onChange={(e, val) => {
        if (val !== null) {
          // buttons can be enabled / disabled - we want to block null
          onChange?.(e, val);
        }
      }}
      {...otherProps}
      size={muiSize}
      exclusive
      className={cx(classes.qSegmentedTabs, className)}
      sharedcomponentid='QSegmentedTabs'
    >
      {tabChildren.map((toggleButton) => {
        const tabProps = toggleButton.props as React.ComponentProps<typeof ToggleButton>;
        const { classes: tabClasses, children: tabLabel, ...restTabProps } = tabProps;
        return (
          <ToggleButton
            key={toggleButton.key ?? String(tabProps.value)}
            {...restTabProps}
            disableRipple
            classes={{
              ...tabClasses,
              root: cx(classes.toggleButton, tabClasses?.root),
              selected: cx(classes.selected, tabClasses?.selected),
            }}
          >
            <span className={classes.toggleButtonLabel}>
              {Children.map(tabLabel, (child) =>
                typeof child === 'string' || typeof child === 'number' ? (
                  <span className={classes.toggleButtonLabelText}>{child}</span>
                ) : (
                  child
                ),
              )}
            </span>
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
};

export default QSegmentedTabs;
